import React, { useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from "@mui/material";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import DialogComponent from "components/DialogComponent";

const EXECUTABLE_EXTENSIONS = [
    ".exe", ".msi", ".bat", ".cmd", ".com", ".scr", ".pif",
    ".php", ".js", ".jsp", ".asp", ".aspx", ".py", ".rb", ".sh", ".ts",
    ".dll", ".sys", ".pdb", ".jar", ".app", ".dmg",
];

const MAX_PARALLEL_UPLOADS = 4;

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "-";
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 B";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const val = bytes / Math.pow(1024, i);
    return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const getRelativePath = (file) => file.webkitRelativePath || file.name;

const isBlockedFile = (file) => {
    const name = file.name.toLowerCase();
    return EXECUTABLE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const runWithConcurrency = async (items, limit, worker) => {
    let cursor = 0;

    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (cursor < items.length) {
            const currentIndex = cursor;
            cursor += 1;
            await worker(items[currentIndex], currentIndex);
        }
    });

    await Promise.all(runners);
};

const waitForJobToFinish = async (jobId, getJob) => {
    if (!getJob) return;

    const maxAttempts = 60;
    const delayMs = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const response = await getJob(jobId).unwrap();
        const status = response?.data?.status;

        if (status === "Completed" || status === "CompletedWithErrors" || status === "Failed") {
            return response?.data;
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    throw new Error("Folder upload is still processing. Please refresh DMS in a few minutes.");
};

const UploadFolder = ({
    open,
    handleClose,
    projectId,
    parentFolderId,
    createJob,
    completeJob,
    getJob,
    onSuccess,
}) => {
    const inputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedCount, setUploadedCount] = useState(0);
    const [failedFiles, setFailedFiles] = useState([]);
    const [jobStatus, setJobStatus] = useState("");

    const blockedFiles = useMemo(() => files.filter(isBlockedFile), [files]);
    const allowedFiles = useMemo(() => files.filter((file) => !isBlockedFile(file)), [files]);
    const totalBytes = useMemo(() => allowedFiles.reduce((sum, file) => sum + file.size, 0), [allowedFiles]);
    const progress = allowedFiles.length > 0 ? Math.round((uploadedCount / allowedFiles.length) * 100) : 0;

    const resetState = () => {
        setFiles([]);
        setIsUploading(false);
        setUploadedCount(0);
        setFailedFiles([]);
        setJobStatus("");
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const closeDialog = () => {
        if (isUploading) return;
        resetState();
        handleClose();
    };

    const handleSelectFiles = (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        setFiles(selectedFiles);
        setUploadedCount(0);
        setFailedFiles([]);
        setJobStatus("");
    };

    const handleUpload = async () => {
        if (!projectId || !parentFolderId || allowedFiles.length === 0) return;

        setIsUploading(true);
        setUploadedCount(0);
        setFailedFiles([]);

        try {
            setJobStatus("Creating upload job...");
            const payload = {
                projectId,
                parentFolderId,
                files: allowedFiles.map((file) => ({
                    fileName: file.name,
                    relativePath: getRelativePath(file),
                    contentType: file.type || "application/octet-stream",
                    fileSize: file.size,
                })),
            };

            const jobResponse = await createJob(payload).unwrap();
            const job = jobResponse?.data;
            const uploadTargets = job?.files || [];
            const filesByPath = new Map(allowedFiles.map((file) => [getRelativePath(file), file]));
            const failures = [];

            setJobStatus("Uploading files to Azure...");
            await runWithConcurrency(uploadTargets, MAX_PARALLEL_UPLOADS, async (target) => {
                const file = filesByPath.get(target.relativePath);
                if (!file) {
                    failures.push({ path: target.relativePath, reason: "Local file not found" });
                    return;
                }

                try {
                    const response = await fetch(target.uploadUrl, {
                        method: "PUT",
                        headers: {
                            "x-ms-blob-type": "BlockBlob",
                            "Content-Type": file.type || "application/octet-stream",
                        },
                        body: file,
                    });

                    if (!response.ok) {
                        throw new Error(`Azure upload failed (${response.status})`);
                    }

                    setUploadedCount((count) => count + 1);
                } catch (error) {
                    failures.push({
                        path: target.relativePath,
                        reason: error?.message || "Upload failed",
                    });
                }
            });

            if (failures.length > 0) {
                setFailedFiles(failures);
                setJobStatus("Some files failed to upload.");
                return;
            }

            setJobStatus("Finalizing upload job...");
            await completeJob(job.jobId).unwrap();
            setJobStatus("Processing files in DMS...");
            const completedJob = await waitForJobToFinish(job.jobId, getJob);

            if (completedJob?.status === "Failed") {
                throw new Error(completedJob?.errorMessage || "Folder upload processing failed.");
            }

            setJobStatus("Upload completed.");
            await onSuccess?.(job.jobId, completedJob);
            resetState();
            handleClose();
        } catch (error) {
            setFailedFiles([
                {
                    path: "Folder upload",
                    reason: error?.data?.message || error?.message || "Upload failed",
                },
            ]);
            setJobStatus("Upload failed.");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <DialogComponent title="Upload Folder" open={open} handleClose={closeDialog} maxWidth="md">
            <Box sx={{ p: 1 }}>
                <Box
                    component="label"
                    sx={{
                        border: "2px dashed #E5E7EB",
                        borderRadius: "3px",
                        p: 5,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: isUploading ? "default" : "pointer",
                        backgroundColor: files.length ? "rgba(0, 52, 76, 0.04)" : "transparent",
                        "&:hover": {
                            borderColor: isUploading ? "#E5E7EB" : "#00344C",
                            backgroundColor: isUploading ? undefined : "rgba(0, 52, 76, 0.02)",
                        },
                    }}
                >
                    <input
                        hidden
                        ref={inputRef}
                        type="file"
                        multiple
                        webkitdirectory=""
                        directory=""
                        disabled={isUploading}
                        onChange={handleSelectFiles}
                    />
                    <FolderZipOutlinedIcon sx={{ fontSize: 48, color: "#00344C", opacity: 0.7, mb: 2 }} />
                    <Typography sx={{ fontFamily: "Outfit", fontWeight: 500, color: "#374151" }}>
                        {files.length ? `${allowedFiles.length} files selected` : "Click to select a folder"}
                    </Typography>
                    {files.length > 0 && (
                        <Typography variant="caption" sx={{ mt: 1, color: "#6B7280" }}>
                            {formatBytes(totalBytes)}
                        </Typography>
                    )}
                </Box>

                {blockedFiles.length > 0 && (
                    <Typography sx={{ mt: 1, color: "#B42318", fontSize: "13px" }}>
                        {blockedFiles.length} blocked file{blockedFiles.length === 1 ? "" : "s"} will be skipped.
                    </Typography>
                )}

                {isUploading && (
                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                            <Typography sx={{ fontSize: "13px", color: "#374151" }}>{jobStatus}</Typography>
                            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                                {uploadedCount}/{allowedFiles.length}
                            </Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={progress} />
                    </Box>
                )}

                {failedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#B42318" }}>
                            Failed files
                        </Typography>
                        <List dense sx={{ maxHeight: 160, overflow: "auto" }}>
                            {failedFiles.map((file, index) => (
                                <ListItem key={`${file.path}-${index}`} disableGutters>
                                    <ListItemText
                                        primary={file.path}
                                        secondary={file.reason}
                                        primaryTypographyProps={{ fontSize: 12 }}
                                        secondaryTypographyProps={{ fontSize: 12 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        mt: 3,
                        pt: 2,
                        borderTop: "1px solid #F3F4F6",
                    }}
                >
                    <Button
                        onClick={closeDialog}
                        disabled={isUploading}
                        sx={{
                            textTransform: "none",
                            color: "#F15744",
                            fontSize: "14px",
                            fontFamily: "Outfit, sans-serif",
                            borderRadius: "25px",
                            fontWeight: 500,
                            px: "20px",
                            py: "15px",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={isUploading || allowedFiles.length === 0 || !parentFolderId}
                        onClick={handleUpload}
                        sx={{
                            backgroundColor: "#00344C",
                            borderRadius: "25px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "14px",
                            fontFamily: "Outfit, sans-serif",
                            color: "#FFFFFF",
                            px: "20px",
                            py: "15px",
                        }}
                    >
                        {isUploading ? "Uploading..." : "Upload Folder"}
                    </Button>
                </Box>
            </Box>
        </DialogComponent>
    );
};

export default UploadFolder;
